<script setup lang="ts">
import DeliveryOption from '@/components/contents/interim/components/configure/DeliveryOption.vue';
import PrimaryButton from '@/components/contents/interim/components/common/PrimaryButton.vue';
import SelectionField from '@/components/contents/interim/components/configure/SelectionField.vue';
import { useForm } from '@inertiajs/vue3';
import { ref, watch } from 'vue';
import { useVoucherCatalog } from '@/composables/useVoucherCatalog';
import { trackAddToCart } from '@/utils/ga4EventHelpers';
import { trackPlausibleAddToCart } from '@/utils/plausibleEventHelpers';

const props = defineProps<{
    defaultAmount?: number | null;
    defaultType?: 'digital' | 'physical' | null;
}>();

const { amountOptions, defaultAmount } = useVoucherCatalog();
const amounts = amountOptions;
const quantities = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

function amountToSelectString(amount: number | null | undefined): string {
    if (amount == null) {
        return String(defaultAmount.value);
    }
    const s = String(amount);
    return amounts.value.includes(s) ? s : String(defaultAmount.value);
}

function typeToSelectedType(type: 'digital' | 'physical' | null | undefined): 'pdf' | 'post' {
    return type === 'physical' ? 'post' : 'pdf';
}

const selectedType = ref<'pdf' | 'post'>(typeToSelectedType(props.defaultType));
const amount = ref(amountToSelectString(props.defaultAmount));
const quantity = ref('1');

watch(
    () => [props.defaultAmount, props.defaultType] as const,
    ([nextAmount, nextType]) => {
        amount.value = amountToSelectString(nextAmount);
        selectedType.value = typeToSelectedType(nextType);
    },
);

const form = useForm({
    type: 'digital' as 'digital' | 'physical',
    amount: defaultAmount.value,
    quantity: 1,
    background: 'default',
    greeting: '',
    personalNote: '',
});

const updateFormType = (): void => {
    form.type = selectedType.value === 'pdf' ? 'digital' : 'physical';
};

const updateFormAmount = (): void => {
    form.amount = parseInt(amount.value, 10);
};

const updateFormQuantity = (): void => {
    form.quantity = parseInt(quantity.value, 10);
};

const addToCart = (): void => {
    updateFormType();
    updateFormAmount();
    updateFormQuantity();

    trackAddToCart({
        type: form.type,
        amount: form.amount,
        background: 'default',
        greeting: '',
        personalNote: '',
        quantity: form.quantity,
    });
    trackPlausibleAddToCart({
        type: form.type,
        amount: form.amount,
    });

    form.post(route('cart.add'), {
        preserveScroll: true,
    });
};
</script>

<template>
    <section class="rounded-md bg-white p-6 shadow-md">
        <div class="mb-6 flex flex-col gap-2">
            <h2 class="text-xl font-semibold text-[#004646] xl:text-2xl">Gutschein wählen</h2>
            <p class="font-open text-sm text-[#393734]/80">
                Betrag und Versandart wählen – Gruß und Design legst du später im Warenkorb fest.
            </p>
        </div>

        <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-5 sm:flex-row sm:flex-wrap">
                <div class="sm:flex-1">
                    <DeliveryOption
                        v-model="selectedType"
                        value="pdf"
                        title="Download als PDF"
                        subtitle="kostenlos – Zuhause direkt ausdrucken"
                        second-subtitle="Im Warenkorb Gruß anpassen und Nachricht hinzufügen"
                        image="/images/redesign/digital_sample.webp"
                        @update:model-value="updateFormType"
                    />
                </div>
                <div class="sm:flex-1">
                    <DeliveryOption
                        v-model="selectedType"
                        value="post"
                        title="Karte per Post"
                        subtitle="zzgl. 4,90 € inkl. Geschenkverpackung"
                        secondSubtitle="1 Werktag Bearbeitung + Versand"
                        image="/images/redesign/physical_sample.webp"
                        @update:model-value="updateFormType"
                    />
                </div>
            </div>

            <div class="flex flex-col gap-5 sm:flex-row">
                <div class="flex-1">
                    <SelectionField v-model="amount" label="Gutschein-Betrag" :items="amounts" suffix="€" @update:model-value="updateFormAmount" />
                </div>
                <div class="min-w-[123px] sm:max-w-[160px]">
                    <SelectionField v-model="quantity" label="Anzahl" :items="quantities" @update:model-value="updateFormQuantity" />
                </div>
            </div>

            <PrimaryButton :on-click="addToCart" :disabled="form.processing">
                {{ form.processing ? 'Wird hinzugefügt...' : 'In den Warenkorb' }}
            </PrimaryButton>
        </div>
    </section>
</template>

<style scoped></style>
